import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { useLocation } from "wouter";
import { GymHeader } from "@/components/GymHeader";
import { toast } from "sonner";
import { Dumbbell, CheckCircle } from "lucide-react";

export default function GymRegisterComplete() {
  const [, setLocation] = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);

  const utils = trpc.useUtils();
  const { data: profile, isLoading: profileLoading } = trpc.gym.member.profile.useQuery(
    undefined,
    { enabled: !!user }
  );

  const registerMutation = trpc.gym.member.register.useMutation({
    onSuccess: () => {
      toast.success("Mitgliedsanfrage erfolgreich gesendet!");
      utils.gym.member.profile.invalidate();
      utils.auth.me.invalidate();
      setLocation("/gym/register");
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  if (authLoading || profileLoading) {
    return (
      <div className="min-h-screen bg-background">
        <GymHeader />
        <div className="container py-12 text-center">
          <p className="text-muted-foreground">Laden...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    setLocation("/gym/register");
    return null;
  }

  // Redirect if already approved or if pending with phone (profile completed)
  const gymStatus = profile?.gymStatus;
  const isGymMember = profile?.roles?.includes("gym_member");
  const hasPhone = profile?.phone && profile.phone.trim() !== "";
  
  if (isGymMember && gymStatus === "approved") {
    setLocation("/gym/register");
    return null;
  }

  // Only redirect if pending AND has phone (profile already completed)
  if (gymStatus === "pending" && hasPhone) {
    setLocation("/gym/register");
    return null;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Bitte geben Sie Ihren Namen ein");
      return;
    }
    
    if (!phone.trim()) {
      toast.error("Bitte geben Sie Ihre Telefonnummer ein");
      return;
    }
    
    if (!agbAccepted) {
      toast.error("Bitte akzeptieren Sie die AGB");
      return;
    }

    registerMutation.mutate({
      name: name.trim(),
      phone: phone.trim(),
      smsConsent,
      agbAccepted
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <GymHeader />
      
      <div className="container py-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <Dumbbell className="h-16 w-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-bold">Profil vervollständigen</h1>
            <p className="text-muted-foreground mt-2">
              Bitte vervollständigen Sie Ihr Profil, um die Gym-Mitgliedschaft zu beantragen
            </p>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Ihre Daten</CardTitle>
              <CardDescription>
                Diese Informationen werden für Ihre Gym-Mitgliedschaft benötigt
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Pre-filled OAuth data */}
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">
                      Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Ihr vollständiger Name"
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Bitte geben Sie Ihren vollständigen Namen ein
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="email">E-Mail</Label>
                    <Input
                      id="email"
                      type="email"
                      value={user.email || ""}
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Aus Ihrem Konto übernommen
                    </p>
                  </div>
                </div>

                {/* Required fields */}
                <div className="space-y-4 pt-4 border-t">
                  <div>
                    <Label htmlFor="phone">
                      Telefonnummer <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="+49 123 456789"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Für wichtige Benachrichtigungen
                    </p>
                  </div>

                  <div className="flex items-start space-x-3 pt-2">
                    <Checkbox
                      id="smsConsent"
                      checked={smsConsent}
                      onCheckedChange={(checked) => setSmsConsent(checked as boolean)}
                    />
                    <div className="space-y-1 leading-none">
                      <Label
                        htmlFor="smsConsent"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Ich möchte SMS-Benachrichtigungen erhalten (optional)
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Erhalten Sie Erinnerungen und wichtige Updates per SMS
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 pt-2">
                    <Checkbox
                      id="agbAccepted"
                      checked={agbAccepted}
                      onCheckedChange={(checked) => setAgbAccepted(checked as boolean)}
                      required
                    />
                    <div className="space-y-1 leading-none">
                      <Label
                        htmlFor="agbAccepted"
                        className="text-sm font-normal cursor-pointer"
                      >
                        Ich akzeptiere die AGB <span className="text-destructive">*</span>
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Mit der Registrierung stimmen Sie unseren Nutzungsbedingungen und der Datenschutzerklärung zu
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setLocation("/gym")}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1"
                    disabled={registerMutation.isPending || !agbAccepted}
                  >
                    {registerMutation.isPending ? "Wird gesendet..." : "Mitgliedschaft beantragen"}
                  </Button>
                </div>

                <p className="text-xs text-muted-foreground text-center">
                  <span className="text-destructive">*</span> Pflichtfelder
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
